import { Component } from '@theme/component';

export class MegaStoreComponent extends Component {
  #loaded = {};
  #pending = {};
  #preloaded = false;
  #controllers = new Set();
  #preloadActive = 0;
  #observer = null;
  #mutationRaf = 0;
  /* Identity of the first panel the component currently binds to. Used to
     detect when soft navigation re-renders the header and replaces the panel
     nodes under a *preserved* <mega-store-component> host — connectedCallback
     does not re-run for a preserved element, so we re-bind + re-inject on
     structural mutation instead. */
  #panelSignature = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
    /* Watch for the host's panels being swapped out (header morph on cart
       refresh / any non-navigation re-render). Must not re-bind on every card
       injection, so the handler only reacts when the panel-node identity changed. */
    this.#observer = new MutationObserver(() => this.#handleMutation());
    this.#observer.observe(this, { childList: true, subtree: true });
    /* Soft navigation re-renders the header but preserves the host element, so
       connectedCallback does not re-run. Re-sync after each navigation ends. */
    document.addEventListener('site:navigation:end', this.#onNavEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    document.removeEventListener('site:navigation:end', this.#onNavEnd);
    if (this.#mutationRaf) cancelAnimationFrame(this.#mutationRaf);
    /* Abort every in-flight panel fetch: the component may be replaced or
       the user may have navigated away; weak networks must not keep paying
       for requests nobody is waiting on. */
    for (const controller of this.#controllers) {
      controller.abort();
    }
    this.#controllers.clear();
  }

  #onNavEnd = () => {
    /* The new header's panels may be committed around this event; deffer to
       the next frame so we see the final tree. Re-injecting is idempotent
       (guarded by #loaded + grid contents), so a re-run here is cheap. */
    if (this.#mutationRaf) cancelAnimationFrame(this.#mutationRaf);
    this.#mutationRaf = requestAnimationFrame(() => {
      this.#mutationRaf = 0;
      this.#preloaded = false;
      this.#init();
    });
  };

  #init() {
    const tabs = [...this.querySelectorAll('[data-store-tab]')];
    const panels = [...this.querySelectorAll('[data-store-panel]')];
    this.#panelSignature = panels[0] || null;
    this.tabs = tabs;
    this.panels = panels;

    tabs.forEach((tab) => {
      const idx = tab.dataset.storeTab;
      tab.setAttribute('on:pointerenter', `mega-store/activateTab?idx=${idx}`);
      tab.setAttribute('on:focus', `mega-store/activateTab?idx=${idx}`);
    });

    const active = panels.find((p) => p.classList.contains('is-active')) || panels[0];
    if (active) {
      const handle = active.dataset.collectionHandle;
      if (handle) this.#injectPanel(handle, active);
    }

    this.#startIdlePreload();
  }

  #handleMutation() {
    if (this.#mutationRaf) return;
    this.#mutationRaf = requestAnimationFrame(() => {
      this.#mutationRaf = 0;
      /* Re-init only when the panel set was replaced; skip identical trees
         (card injection, class toggles) to avoid churn. */
      const first = this.querySelector('[data-store-panel]');
      if (!first || first === this.#panelSignature) return;
      this.#preloaded = false;
      this.#init();
    });
  }

  #extractCards(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return [...temp.querySelectorAll('.mega-store__card')];
  }

  #fetchPanel(handle) {
    if (this.#loaded[handle]) return Promise.resolve(this.#loaded[handle]);
    /* The first panel is injected eagerly on connect, outside the idle
       pump; until its fetch settles, #loaded is still empty. Share the
       in-flight promise so the pump reuses it instead of re-requesting the
       same collection and pushing concurrency past the cap. */
    if (this.#pending[handle]) return this.#pending[handle];
    const controller = new AbortController();
    this.#controllers.add(controller);
    const timeout = setTimeout(() => controller.abort(), 6000);
    const request = (async () => {
      try {
        const res = await fetch(`${window.Shopify.routes.root}collections/${handle}?section_id=mega-menu-products`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Failed to fetch panel: ${res.status}`);
        const html = await res.text();
        const cards = this.#extractCards(html);
        this.#loaded[handle] = cards;
        return cards;
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          console.error('[mega-store]', err);
        }
        return [];
      } finally {
        clearTimeout(timeout);
        this.#controllers.delete(controller);
        delete this.#pending[handle];
      }
    })();
    this.#pending[handle] = request;
    return request;
  }

  async #injectPanel(handle, panel) {
    const grid = panel.querySelector('[data-mega-panel-grid]');
    if (!grid) return;
    if (this.#loaded[handle] && grid.children.length > 1) return;
    const cards = await this.#fetchPanel(handle);
    if (!this.isConnected) return;
    grid.innerHTML = '';
    if (cards.length === 0) {
      grid.classList.remove('is-loading');
      return;
    }
    cards.forEach((card) => grid.appendChild(card));
    grid.classList.remove('is-loading');
  }

  activateTab(_event, data) {
    const idx = data.idx;
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.storeTab === String(idx);
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.storePanel === String(idx);
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        panel.removeAttribute('hidden');
        const handle = panel.dataset.collectionHandle;
        if (handle) this.#injectPanel(handle, panel);
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  #startIdlePreload() {
    if (this.#preloaded) return;
    const run = () => {
      this.#preloaded = true;
      const handles = this.panels
        .map((panel) => panel.dataset.collectionHandle)
        .filter(Boolean);
      /* Weak networks: prefetching every tab at once competes with the
         page itself and with soft-navigation fetches on the same saturated
         links. Fetch breadth-first with a small concurrency cap instead. */
      const CONCURRENCY = 2;
      let index = 0;
      const pump = () => {
        while (this.#preloadActive < CONCURRENCY && index < handles.length) {
          const handle = handles[index++];
          /* Already fetched, or fetched eagerly by the initial #injectPanel
             and still in flight — skip so only the cap counts new
             requests. */
          if (this.#loaded[handle] || this.#pending[handle]) continue;
          this.#preloadActive++;
          this.#fetchPanel(handle).finally(() => {
            this.#preloadActive--;
            pump();
          });
        }
      };
      pump();
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run);
    } else {
      setTimeout(run, 200);
    }
  }
}

if (!customElements.get('mega-store-component')) {
  customElements.define('mega-store-component', MegaStoreComponent);
}
